import { memo, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Grid2 as Grid,
  Paper,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const formatDate = (value) =>
  value ? new Date(value).toLocaleString("en-US") : "—";

const NotaryActivityRangeReport = ({
  notaryId,
  currentSession,
  chartYear,
  onReportData,
}) => {
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [summary, setSummary] = useState({
    completedSessions: 0,
    activeSessions: 0,
    cancelledSessions: 0,
    completedSignerNotarizations: 0,
    ronCompletedSessions: 0,
    ipenCompletedSessions: 0,
  });

  const rangeInvalid = Boolean(start && end && start.isAfter(end));

  const fetchReport = useCallback(async () => {
    if (!notaryId || !currentSession || rangeInvalid) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/notary-session-report`,
        {
          currentSession,
          currentPath: window.location.pathname,
          notaryId,
          page: paginationModel.page,
          rowsPerPage: paginationModel.pageSize,
          fromDate: start ? start.startOf("month").format("YYYY-MM-DD") : "",
          toDate: end ? end.endOf("month").format("YYYY-MM-DD") : "",
          chartYear,
        },
        { withCredentials: true },
      );
      const data = response.data?.data || {};
      setRows(data.rows || []);
      setRowCount(data.totalCount || 0);
      setSummary((previous) => ({ ...previous, ...(data.summary || {}) }));
      onReportData?.(data);
      setError("");
    } catch (requestError) {
      setError("Unable to load the notary session report.");
    } finally {
      setLoading(false);
    }
  }, [
    notaryId,
    currentSession,
    rangeInvalid,
    paginationModel,
    start,
    end,
    chartYear,
    onReportData,
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    setPaginationModel((previous) => ({ ...previous, page: 0 }));
  }, [notaryId, start, end]);

  const columns = useMemo(
    () => [
      {
        field: "projectAddress",
        headerName: "Project Address",
        minWidth: 280,
        flex: 1.8,
      },
      { field: "county", headerName: "County", minWidth: 120, flex: 0.7 },
      { field: "sessionType", headerName: "Type", minWidth: 90, flex: 0.5 },
      { field: "status", headerName: "Status", minWidth: 130, flex: 0.7 },
      {
        field: "sessionDate",
        headerName: "Activity Date",
        minWidth: 180,
        flex: 1,
        valueFormatter: (value) => formatDate(value),
      },
      {
        field: "signers",
        headerName: "Notarized Signers",
        minWidth: 240,
        flex: 1.4,
      },
    ],
    [],
  );

  const clear = () => {
    setStart(null);
    setEnd(null);
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Typography align="center" variant="h5">
          RON / IPEN Notary Sessions
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Grid
          container
          spacing={1.5}
          alignItems="center"
          justifyContent="center"
        >
          <Grid size={{ xs: 12, sm: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Month"
                format="MMMM YYYY"
                views={["year", "month"]}
                value={start}
                onChange={setStart}
                maxDate={end || dayjs()}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="End Month"
                format="MMMM YYYY"
                views={["year", "month"]}
                value={end}
                onChange={setEnd}
                minDate={start || undefined}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: rangeInvalid,
                    helperText: rangeInvalid ? "End must be after start" : "",
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid size={{ xs: 12, sm: "auto" }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={clear}
              disabled={!start && !end}
            >
              Show All
            </Button>
          </Grid>
        </Grid>
      </Grid>

      {error && (
        <Grid size={{ xs: 12 }}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}

      {[
        ["Completed Sessions", summary.completedSessions],
        ["Active Started", summary.activeSessions],
        ["Cancelled Sessions", summary.cancelledSessions],
        ["Signer Notarizations", summary.completedSignerNotarizations],
        ["RON Completed", summary.ronCompletedSessions],
        ["IPEN Completed", summary.ipenCompletedSessions],
      ].map(([label, value]) => (
        <Grid key={label} size={{ xs: 6, md: 2 }}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
            <Typography variant="overline">{label}</Typography>
            <Typography variant="h4">{value || 0}</Typography>
          </Paper>
        </Grid>
      ))}

      <Grid size={{ xs: 12 }}>
        <Box sx={{ width: "100%" }}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            disableRowSelectionOnClick
            sx={{
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#333",
                color: "white",
              },
              "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within":
                { outline: "none" },
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default memo(NotaryActivityRangeReport);
